/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-misused-promises */

'use client'

import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import { useUserData } from '@utils/userUserData'
import { isLoggedInUtil } from '@utils/utils'
import assetLoader from '@utils/internalImageLoader'
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button'
import StyledComponentsRegistry from '@configs/registry'
import { validateVehicleNumber } from '@components/MainWidget/FastagWidget/helper'
import { vehicleInfoService } from '@services/vehicleInfoService'
import { ThemeProvider, AckoMainTheme } from '@acko-tech/building-blocks.ui.theme'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import {
  INPUT_ICON,
  INPUT_ICON_LOGO,
  TAP_BTN_EVENT,
  PHONE_NUMBER_ENTERED,
} from '@components/MainWidget/FastagWidget/constants'
import CreateLead from '@services/createLead'
import { segmentEventCommonPropFunc } from '@utils/segmentProperties'
import {
  VAS_WIDGET_INPUT_PLACEHOLDER,
  VIEW_CHALLANS,
  CHALLAN_WIDGET_INPUT,
  WIDGET_INPUT,
  REG_NO_ERROR,
  VEHICLE_DATA,
} from '@components/MainWidget/ChallanWidgetLanding/constants'
import ReturningUserInfoBlock from '@components/MainWidget/ChallanWidgetLanding/returningUserInfoBlock'
import MobileNumberModal from '@components/MainWidget/ChallanWidgetModals/mobileNumberInputModal'
import VasAppRedirectWrapper from '@components/MainWidget/ChallanWidgetModals/VasAppRedirectWrapper'
import { newChallanLoadingScreen } from '@components/MainWidget/ChallanWidgetModals/constants'
import LoadingScreen from '@components/MainWidget/ChallanWidgetModals/LoadingScreen'
import { getVasFinalScreenData } from '@factories/vasWidgetsFactory'
import {
  getProductName,
  getSourceName,
  getApiKeyLead,
  getVehicleMMVStoreData,
  fetchDataFromLS,
} from '@components/MainWidget/ChallanWidgetLanding/helper'
import { processAssetData } from '@utils/assethelper'
import styles from './ChallanWidgetLanding.module.scss'
import { FETCH_URL } from '../common/commonConstants'

interface ChallanWidgetLandingProps {
  widget: string
  widgetHeading?: string
  regexPatternsData?: object
  widgetCtaText?: string
  inputPlaceholder?: string
  isMobileDevice: boolean
  challanFinalScreenData: object
  widgetOptions: any
  segmentEventCommonProperties?: object
  reqCookie?: string
  isBottomFloater: boolean
  onHide: Function
}

type ChallanUserData = {
  mobile_number: string
  reg_number: string
  vehicleImage?: string
}

const ChallanWidgetLanding: React.FC<ChallanWidgetLandingProps> = data => {
  const {
    widget,
    widgetHeading,
    regexPatternsData,
    widgetCtaText,
    inputPlaceholder,
    isMobileDevice,
    challanFinalScreenData,
    widgetOptions,
    segmentEventCommonProperties,
    isBottomFloater,
    onHide,
  } = data

  const challanWrapperProps = getVasFinalScreenData(challanFinalScreenData)
  const [regNo, setRegNo] = useState('')
  const [error, setError] = useState('')
  const isLoggedIn: boolean = isLoggedInUtil(data?.reqCookie)
  const userData = useUserData(isLoggedIn)
  const [returningUserResponse, setReturningUserResponse] = useState<any>(null)
  const [challanUserData, setChallanUserData] = useState<ChallanUserData>({
    mobile_number: '',
    reg_number: '',
    vehicleImage: '',
  })
  const [showChallanLoading, setShowChallanLoading] = useState<boolean>(false)
  const [showMobileNumberModal, setShowMobileNumberModal] = useState<boolean>(false)
  const [showMobileChallanModal, setShowMobileChallanModal] = useState<boolean>(false)
  const [showMwebChallanModal, setShowMwebChallanModal] = useState<boolean>(true)
  const [showDoMoreThanChallanModal, setShowDoMoreThanChallanModal] = useState<boolean>(false)
  const inputPlaceHolder = inputPlaceholder || VAS_WIDGET_INPUT_PLACEHOLDER
  const inputRef = useRef<HTMLInputElement | null>(null)
  const config = useEnvVariablesClientConfig()
  const currentEnv = config?.ACKO_ENV
  const challanFallbackScreenData = widgetOptions?.challanFallbackScreenData

  const [properties, setProperties] = useState<any>(null)

  useEffect(() => {
    const fetchProperties = async () => {
      const resolvedProperties = await segmentEventCommonPropFunc(segmentEventCommonProperties)
      setProperties(resolvedProperties)
    }
    fetchProperties()
  }, [segmentEventCommonProperties])

  useEffect(() => {
    const getLocalStorageData = async () => {
      const localStorageData = await fetchDataFromLS(VEHICLE_DATA)
      setReturningUserResponse(localStorageData)
      setChallanUserData({ ...challanUserData, mobile_number: localStorageData?.mobile_number })
    }
    getLocalStorageData()
  }, [showChallanLoading])

  const handleReturningSearch = () => {
    setChallanUserData(returningUserResponse)
    setShowChallanLoading(true)
    onSubmitEvent(returningUserResponse)
  }

  const fetchVehicleAndProceed = async (regNumber, mobileNumber) => {
    if (!regNumber) {
      return
    }
    try {
      setShowChallanLoading(true)
      const vehicleInfo = await vehicleInfoService(regNumber)
      let imageResponse
      try {
        imageResponse = await processAssetData(vehicleInfo, FETCH_URL)
      } catch (error) {
        console.error('Error fetching image response info:', error, regNumber)
      }
      setChallanUserData(prev => ({
        ...prev,
        reg_number: regNumber,
        mobile_number: mobileNumber,
        vehicleInfo,
        vehicleImage: imageResponse?.variantDetails?.image,
      }))
    } catch (error) {
      console.error('Error fetching vehicle info:', error, regNumber)
    }
  }

  const handleRegNoChange = e => {
    const newRegNumber = e?.target?.value.toUpperCase()
    setRegNo(newRegNumber)
    setChallanUserData({ ...challanUserData, reg_number: newRegNumber })
  }

  const onSubmitEvent = data => {
    trackEvent(TAP_BTN_EVENT, {
      ...properties,
      ...{ journey: 'challan' },
      product: getProductName(),
      phone: data?.mobile_number || '',
      registration_number: data?.reg_number || '',
    })
  }

  const onFormSubmit = e => {
    setError('')
    e.preventDefault()
    inputRef?.current?.blur()
    const vehicleError = validateVehicleNumber(regNo, regexPatternsData)
    if (!regNo || vehicleError) {
      setError(REG_NO_ERROR)
      return
    }
    onSubmitEvent(challanUserData)

    if (isLoggedIn && userData) {
      fetchVehicleAndProceed(regNo, userData?.phone)
      return
    }
    if (!challanUserData?.mobile_number) {
      setShowMobileNumberModal(true)
    } else {
      fetchVehicleAndProceed(regNo, challanUserData?.mobile_number)
      CreateLead({
        registrationNumber: regNo,
        phoneNo: challanUserData?.mobile_number,
        productKey: getProductName(),
        sourceName: getSourceName(),
        apiKey: getApiKeyLead(currentEnv),
      })
    }
  }

  const handleMobileNumberSubmit = async (mobileNumber: string) => {
    setShowMobileNumberModal(false)
    fetchVehicleAndProceed(regNo, mobileNumber)
    trackEvent(PHONE_NUMBER_ENTERED, {
      ...properties,
      ...{ journey: 'challan' },
      product: getProductName(),
      phone: mobileNumber || '',
      registration_number: regNo,
    })
    CreateLead({
      registrationNumber: regNo,
      phoneNo: mobileNumber,
      productKey: getProductName(),
      sourceName: getSourceName(),
      apiKey: getApiKeyLead(currentEnv),
    })
  }

  const handleLoadingComplete = async () => {
    await getVehicleMMVStoreData(challanUserData)
    setShowChallanLoading(false)
    setShowMobileChallanModal(true)
    setShowMwebChallanModal(true)
  }

  const handleFallbackModal = () => {
    setShowChallanLoading(false)
    setShowDoMoreThanChallanModal(false)
    setShowMobileChallanModal(false)
    setRegNo('')
    setError('')
  }

  return (
    <ThemeProvider theme={AckoMainTheme}>
      <>
        <div>
          {showChallanLoading && (
            <LoadingScreen
              isMobile={isMobileDevice}
              screens={newChallanLoadingScreen}
              duration={1200}
              regNo={regNo || challanUserData?.reg_number}
              onComplete={() => handleLoadingComplete()}
              isBottomFloater={isBottomFloater}
              showLoadingScreen={showChallanLoading}
            />
          )}

          {showMobileChallanModal && (
            <VasAppRedirectWrapper
              mobileNo={challanUserData?.mobile_number}
              isMobile={isMobileDevice}
              vasWrapperProps={challanWrapperProps}
              vasFallbackScreenData={challanFallbackScreenData}
              regNum={challanUserData?.reg_number}
              vehicleInfoData={challanUserData}
              segmentEventCommonProperties={properties}
              setShowMobileChallanModal={handleFallbackModal}
              isBottomFloater={isBottomFloater}
              onHide={onHide}
              showMwebChallanModal={showMwebChallanModal}
              setShowMwebChallanModal={setShowMwebChallanModal}
              showDoMoreThanChallanModal={showDoMoreThanChallanModal}
              setShowDoMoreThanChallanModal={setShowDoMoreThanChallanModal}
              widgetType={widget}
            />
          )}
          {showMobileNumberModal && (
            <MobileNumberModal
              isMobile={isMobileDevice}
              showMobileNumberModal={showMobileNumberModal}
              setShowMobileNumberModal={setShowMobileNumberModal}
              onSubmit={(mobileNumber: string) => {
                handleMobileNumberSubmit(mobileNumber).catch(console.error)
              }}
              widgetType={widget}
            />
          )}
        </div>
        <div id="challan-widget-landing-widget" className={styles.widgetRootContainer}>
          {returningUserResponse?.vehicleInfo?.registration_number && (
            <ReturningUserInfoBlock userInfo={returningUserResponse} handleReturningSearch={handleReturningSearch} />
          )}
          <form className={`${styles.widgetForm}`} onSubmit={e => onFormSubmit(e)}>
            <label htmlFor={WIDGET_INPUT} className={styles.widgetHeading}>
              {widgetHeading}
            </label>
            <div className={error ? styles.inputErrorStyles : styles.inputBoxContainer}>
              <Image
                width={0}
                height={0}
                src={`${assetLoader()}${INPUT_ICON}`}
                className={styles.inputIcon}
                alt={INPUT_ICON_LOGO}
              />
              <input
                type="text"
                id={CHALLAN_WIDGET_INPUT}
                ref={inputRef}
                placeholder={inputPlaceHolder}
                onChange={handleRegNoChange}
                autoComplete="off"
                value={regNo}
                maxLength={10}
              />
            </div>
            {error && <div className={styles.inputError}>{error}</div>}
            <StyledComponentsRegistry>
              <PrimaryButton className={styles.primaryButton}>{widgetCtaText || VIEW_CHALLANS}</PrimaryButton>
            </StyledComponentsRegistry>
          </form>
        </div>
      </>
    </ThemeProvider>
  )
}

export default ChallanWidgetLanding
